import { Check, AlertTriangle } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal, MaskLines, ClipImage } from "@/components/Motion";
import { CallButton, ConsultButton } from "@/components/Buttons";
import { FaqSection } from "@/components/FaqSection";
import { CTABand } from "@/components/CTABand";
import { SectionHead, usePageMeta } from "@/components/Common";
import { EXTERIOR, INTERIOR, REPORT_ITEMS } from "@/data/site";

const Checklist = ({ title, items, img, alt, flip, testId, idx }) => (
  <div data-testid={testId} className="grid gap-10 lg:grid-cols-12 lg:items-start">
    <div className={`lg:col-span-5 ${flip ? "lg:order-2 lg:col-start-8" : ""}`}>
      <ClipImage src={img} alt={alt} className="h-72 rounded-[4px] md:h-[520px] lg:sticky lg:top-28" />
    </div>
    <div className={`lg:col-span-6 ${flip ? "lg:order-1" : "lg:col-start-7"}`}>
      <p className="eyebrow">{idx} · Applicable observations may include</p>
      <h3 className="mt-4 font-display text-4xl font-semibold text-[#14263D] md:text-5xl">{title}</h3>
      <ul className="mt-8">
        {items.map((it, i) => (
          <Reveal as="li" key={it} delay={i * 0.03} className="group flex items-center gap-5 border-b border-[#14263D]/10 py-4">
            <span className="font-mono text-xs text-[#5F6B76]">{String(i + 1).padStart(2, "0")}</span>
            <span className="flex-1 text-base text-[#14263D] transition-transform duration-300 group-hover:translate-x-1">{it}</span>
            <Check className="h-4 w-4 text-[#A52A32] opacity-60 transition-opacity group-hover:opacity-100" />
          </Reveal>
        ))}
      </ul>
    </div>
  </div>
);

const Reports = () => (
  <section data-testid="reports-section" className="grain relative overflow-hidden bg-[#14263D] py-24 text-white md:py-32">
    <div className="relative mx-auto grid max-w-[1400px] gap-14 px-5 md:px-10 lg:grid-cols-12 lg:items-center">
      <div className="lg:col-span-6">
        <SectionHead light eyebrow="After Every Visit" title={<MaskLines onView lines={["Professional", "Visit Reports"]} />}>
          <p>Following each visit, customers receive a digital property report. Reports may include:</p>
        </SectionHead>
        <ul className="mt-10 grid gap-3 sm:grid-cols-2">
          {REPORT_ITEMS.map((r, i) => (
            <Reveal as="li" key={r} delay={i * 0.05} className="flex items-center gap-3 rounded-[4px] border border-white/15 px-4 py-3 text-sm text-white/90">
              <span className="h-1.5 w-1.5 rounded-full bg-[#A52A32]" />{r}
            </Reveal>
          ))}
        </ul>
      </div>
      <ClipImage src="/images/phone.jpg" alt="Homeowner reading a digital property report on a smartphone" className="h-[420px] rounded-[4px] lg:col-span-5 lg:col-start-8 lg:h-[560px]" />
    </div>
  </section>
);

const Concern = () => (
  <section data-testid="concern-section" className="bg-[#F8F7F3] py-24 md:py-32">
    <div className="mx-auto grid max-w-[1400px] gap-10 px-5 md:px-10 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <SectionHead eyebrow="Clear Communication" title={<MaskLines onView lines={["When We Find", "a Concern"]} />} />
      </div>
      <Reveal className="space-y-5 text-base leading-relaxed text-[#5F6B76] md:text-lg lg:col-span-6 lg:col-start-7">
        <p>If we observe an apparent issue, we will notify the homeowner according to the service agreement and the circumstances.</p>
        <p>When authorized, we can help arrange access for homeowner-approved contractors or service professionals.</p>
        <p className="flex gap-3 rounded-[4px] border border-[#A52A32]/30 bg-white p-5 text-sm text-[#14263D]">
          <AlertTriangle className="h-5 w-5 shrink-0 text-[#A52A32]" />
          We do not diagnose technical problems or guarantee that all problems will be discovered.
        </p>
      </Reveal>
    </div>
  </section>
);

export default function HomeWatch() {
  usePageMeta("Home Watch Services Wellington FL | Seasonal & Snowbird Home Watch | First Due", "Scheduled home watch visits with photo reports for seasonal residents in Wellington, Royal Palm Beach, Westlake, Arden, Loxahatchee & Loxahatchee Groves.");
  return (
    <main data-testid="home-watch-page">
      <PageHero
        testId="home-watch-hero"
        eyebrow="Professional Home Watch Services"
        lines={["Helping You Stay", "Informed While", "You're Away."]}
        intro="Our home watch service consists of scheduled visual observations of accessible areas of your property to identify obvious concerns and communicate our findings. We develop a property-specific checklist based on your home's layout, accessibility, service plan and written instructions."
        img="/images/pool.jpg"
        alt="Backyard pool and palms at a South Florida home"
      >
        <ConsultButton testId="home-watch-hero-consult-btn" label="Request a Home Watch Consultation" dark />
        <CallButton testId="home-watch-hero-call-btn" dark />
      </PageHero>
      <section className="space-y-24 bg-white py-24 md:space-y-32 md:py-32">
        <div className="mx-auto max-w-[1400px] space-y-24 px-5 md:space-y-32 md:px-10">
          <Checklist testId="exterior-checklist" idx="01" title="Exterior Home Watch Observations" items={EXTERIOR} img="/images/mediterranean.jpg" alt="Exterior of a Florida home with landscaping" />
          <Checklist testId="interior-checklist" idx="02" title="Interior Home Watch Observations" items={INTERIOR} img="/images/interior2.jpg" alt="Clean, well-maintained residential interior" flip />
        </div>
      </section>
      <Reports />
      <Concern />
      <FaqSection />
      <CTABand testId="home-watch-cta" title={["Request a Home Watch", "Consultation."]} consultLabel="Request a Home Watch Consultation" />
    </main>
  );
}
